import { TitleCasePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FreudMessage, FreudMessageService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { IProfessionalAddress } from 'app/interfaces/address.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { AddressService } from 'app/services/address.service';
import { UserService } from 'app/services/user.service';

import { LocationService } from '../../../../app/services/location.service';

@Component({
	selector: 'new-address.component',
	templateUrl: './address.component.html',
	styleUrls: ['./address.component.scss'],
	providers: [TitleCasePipe],
})
export class AddressComponent implements OnInit {
	public formInstance: FormGroup = null;
	public isConfirmCancelModalOpen = false;
	public paymentMethods;
	public isLoading = false;
	public action: 'create' | 'edit' = 'create';
	public editId = null;
	public canUpdatePrice = false;
	public initialValue = null;
	public tiers = [];
	public price = 0;
	professionalId = null;
	constructor(
		private router: Router,
		public locationService: LocationService,
		private messageService: FreudMessageService,
		private translateService: TranslateService,
		public activatedRoute: ActivatedRoute,
		public addressService: AddressService,
		private translate: TranslateService,
		private eventTracker: ActionTrackerService,
		private capitalize: TitleCasePipe,
		private user: UserService
	) {
		this.action = this.activatedRoute.snapshot.data.action;
		this.editId =
			this.action === 'edit' ? this.activatedRoute.snapshot.params.id : null;
	}

	ngOnInit(): void {
		this.user.getUserId().then((res) => {
			this.professionalId = res;
		});
		if (this.action === 'edit') {
			this.getAddress();
		}
	}

	getAddress() {
		this.addressService
			.getAddressById(this.editId)
			.toPromise()
			.then((address) => {
				this.initialValue = address;
				this.onChangesTiers(address.professionalAddressPayment.canAcceptTiers);
				this.onChangeParticularPrice(address.professionalAddressPayment.price);
				this.canUpdatePrice = address.professionalAddressPayment.canUpdatePrice;
			})
			.catch(() => {
				this.router.navigateByUrl('/account/onsite-sessions');
			});
	}

	setFormInstance(form) {
		this.formInstance = form;
	}

	onBackPage() {
		this.confirmCancel();
	}

	onChangesTiers(value) {
		this.tiers = value;
	}

	onChangeParticularPrice(price) {
		this.price = price;
	}

	async saveAddress() {
		const data: IProfessionalAddress = {
			...this.formInstance.value,
			professionalAddressPayment: {
				price: this.price,
				canAcceptTiers: this.tiers,
				acceptHalfHour: false,
			},
		};

		if (this.formInstance.invalid) {
			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('PAGES.ADDRESS.VERIFY_FORM_FIELDS'),
				detail: null,
			});
			this.formInstance.markAllAsTouched();
			return;
		}

		this.action === 'edit' ? this.editAddress(data) : this.createAddress(data);
	}

	async editAddress(data) {
		return this.addressService
			.updateAddress(this.editId, data)
			.toPromise()
			.then((res) => {
				this.toast(
					'success',
					this.translateService.instant(`PAGES.ADDRESS.TOAST_SUCCESS_EDIT`)
				);
				this.trackSubmit('success', res);
				this.backToAccountPage();
			})
			.catch((error) => {
				this.trackSubmit('error', error);
				this.toast('error', this.translateService.instant(error.messageI18n));
			})
			.finally(() => {
				this.isLoading = false;
			});
	}

	async createAddress(data) {
		this.addressService
			.createAddress(data)
			.toPromise()
			.then((res) => {
				this.toast(
					'success',
					this.translateService.instant(`PAGES.ADDRESS.TOAST_SUCCESS_CREATE`)
				);
				this.trackSubmit('success', res);
				this.backToAccountPage();
			})
			.catch((err) => {
				this.toast(
					'error',
					this.translateService.instant(`PAGES.ADDRESS.TOAST_ERROR_CREATE`)
				);
				this.trackSubmit('error', err);
			})
			.finally(() => {
				this.isLoading = false;
			});
	}

	trackSubmit(res: 'success' | 'error', data = { error: null, id: null }) {
		this.eventTracker.eventTracker(
			SCOPE_TYPE.Account,
			SUBSCOPE_TYPE.Address,
			ITEM_TYPE[this.capitalize.transform(this.action)],
			INTERACTION_TYPE[this.capitalize.transform(res)],
			{
				professionalId: this.professionalId,
				price: this.price,
				city: this.formInstance.controls.city.value,
				state: this.formInstance.controls.state.value,
				canAcceptTiers: this.tiers,
				error: res === 'error' ? data.error.statusCode : null,
				addressId: data.id,
			}
		);
	}

	confirmCancel() {
		if (this.formInstance.dirty) {
			this.isConfirmCancelModalOpen = true;
			return;
		}
		this.backToAccountPage();
	}

	backToAccountPage() {
		this.router.navigateByUrl('/account/onsite-sessions');
	}

	toast(type: 'success' | 'error', message) {
		const toast: FreudMessage = {
			severity: type,
			summary: message,
		};
		this.messageService.add(toast);
	}
}
